import { Box } from "@mui/material";
import { Switch, Divider, Checkbox } from "antd";
import { useEffect, useState } from "react";
import SpaceFolderGrid from "components/grid";
import FileGrid from "components/ObjectList";
import {
  fetchMySpaces,
} from "./spaceListApi";
import { useMsal, IMsalContext } from "@azure/msal-react";
import { NormalizedSpace } from "utils/normalizeSpaces";
import "./style.scss";
import LinksSection from "components/Links";
import { SpaceFilters } from "pages/Explorer";
import { getS3grantAccessToken } from "config/token";

// Dummy data to simulate file listing per space
const dummyFiles: Record<string, string[]> = {
  folder1: ["fileA.txt", "fileB.txt", "fileC.txt", "fileD.txt", "fileE.txt", "fileF.txt", "fileG.txt", "fileH.txt"],
  folder2: ["image1.jpg", "image2.jpg"],
  "test-folder": ["doc1.pdf", "doc2.pdf"],
  "test-folder1": ["data.csv"],
  "test-folder2": ["notes.md", "presentation.pptx"],
  "test-folder3": [],
};

export interface ISusData {
  gitRepo: string,
  projectId: string,
  projectLink: string,
  susProjectMetadata: string,
}

interface SortOptions {
  sortFields: Array<{ field: string; order: "asc" | "desc" } | string>;
}

interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const SpaceList = ({
  filters,
  spaceList,
  domainName,
  pagination,
  searchTerm,
  sortOptions,
  onSortChange,
  onRefreshSpaces,
  onFiltersChange,
  onPaginationChange,
  onSearchInputChange,
  handleAutoRefreshSpaces,
  isSpaceListFetching = false,
  refreshKey,
}: {
  domainName: any;
  onRegionChange: any;
  refreshKey: number;
  searchTerm: string;
  filters: SpaceFilters;
  sortOptions: SortOptions;
  spaceList: NormalizedSpace[];
  pagination: PaginationState;
  onRefreshSpaces: () => void;
  isSpaceListFetching: boolean;
  handleAutoRefreshSpaces: () => void;
  onSearchInputChange: (value: string) => void;
  onSortChange: (sort: Partial<SortOptions>) => void;
  onFiltersChange: (filters: Partial<SpaceFilters>) => void;
  onPaginationChange: (page: number, pageSize?: number) => void;
}) => {
  const [bucketData, setBucketData] = useState<Record<string, string[]>>({});
  const [regionNames, setRegionNames] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(["all"]);
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  // Removed: spaceObjects, loading, apiError. FileGrid now manages its own state.
  const [selectedSpaceRegion, setSelectedSpaceRegion] = useState<string | null>(null);
  const [currentPrefix, setCurrentPrefix] = useState<string>("");
  const [selectedSpaceDisplayName, setSelectedSpaceDisplayName] = useState<string>("");
  const [selectedSpaceBucketName, setSelectedSpaceBucketName] = useState<string>("");
  const [selectedSpaceGxpFlag, setSelectedSpaceGxpFlag] = useState<string>("");
  const [lastSelectedRegion, setLastSelectedRegion] = useState<string>("");
  const [selectedSpaceFileSystemType, setSelectedSpaceFileSystemType] = useState<string>("");
  const [susData, setSusData] = useState<ISusData>({
    gitRepo: "",
    projectId: "",
    projectLink: "",
    susProjectMetadata: "",
  });
  const [selectedDataAccountID, setSelectedDataAccountID] = useState<string>("");
  const [selectedSpaceUserPermissions, setSelectedSpaceUserPermissions] = useState<Array<{ user_email: string; access_level: string; }>>([]);

  const msalInstance = useMsal();

  // Sync local region checkbox state whenever the parent resets filters
  // (e.g. domain change, explicit refresh).
  useEffect(() => {
    setSelectedRegions(filters.selectedRegions);
  }, [filters.selectedRegions, refreshKey]);

  /**
   * Returns the region list to restore when toggling a scope filter OFF.
   * Prefers the last explicitly-selected single region; falls back to "all".
   */
  const getRestoredRegions = (): string[] =>
    selectedRegions[0] === "all" || !lastSelectedRegion || lastSelectedRegion === "all"
      ? ["all"]
      : [lastSelectedRegion];

  const handleMySpaces = (checked: boolean) => {
    setSelectedSpace(null);
    onSearchInputChange("");

    const restoredRegions = getRestoredRegions();

    onFiltersChange({
      isMySpaces: checked,
      isMyAccessibleSpaces: checked ? false : filters.isMyAccessibleSpaces,
      searchSpaceName: "",
      ...(!checked ? { selectedRegions: restoredRegions } : {}),
    });

    if (!checked) {
      setSelectedRegions(restoredRegions);
    }
  };

  const handleMyAccessibleSpaces = (checked: boolean) => {
    setSelectedSpace(null);
    onSearchInputChange("");

    const restoredRegions = getRestoredRegions();

    onFiltersChange({
      isMyAccessibleSpaces: checked,
      isMySpaces: checked ? false : filters.isMySpaces,
      searchSpaceName: "",
      ...(!checked ? { selectedRegions: restoredRegions } : {}),
    });

    if (!checked) {
      setSelectedRegions(restoredRegions);
    }
  };

  // Update domain data loading to work with new state structure
  useEffect(() => {
    const domainsData = JSON.parse(localStorage.getItem("domainsData") || "[]");
    const domainEntry = domainsData.find((d: any) => d.display_name === domainName);

    if (domainEntry && domainEntry.bucket_name) {
      const bucketName = domainEntry.bucket_name as Record<string, string[]>;
      const regions = Object.keys(bucketName);
      const enabledRegions = regions.filter((r) => bucketName[r]?.length > 0);

      setBucketData(bucketName);
      setRegionNames(regions);
    }

    // Clear selected space when domain changes
    setSelectedSpace(null);
    setSelectedSpaceRegion(null);
    setSelectedSpaceDisplayName("");
    setSelectedSpaceBucketName("");
    setSelectedSpaceGxpFlag("");
    setSelectedDataAccountID("");
    setSelectedSpaceFileSystemType("");
  }, [domainName]);

  useEffect(() => {
    if (searchTerm !== "") {
      setSelectedSpace(null);
      setSelectedSpaceRegion(null);
      setSelectedSpaceDisplayName("");
      setSelectedSpaceBucketName("");
      setSelectedSpaceGxpFlag("");
      setSelectedDataAccountID("");
      setSelectedSpaceFileSystemType("");
    }
  }, [searchTerm]);


  // Fix the checkbox change handlers
  const handleRegionsChange = (checkedValues: (string | number)[]) => {
    const values = checkedValues as string[];

    if (values.length === 1 && values[0] !== "all") {
      setLastSelectedRegion(values[0]);
    }

    let newSelectedRegions: string[];
    if (values.includes("all") || values.length === 0) {
      newSelectedRegions = ["all"];
    } else {
      newSelectedRegions = values;
    }

    setSelectedRegions(newSelectedRegions); // Add this line to update local state

    onFiltersChange({
      selectedRegions: newSelectedRegions,
      searchSpaceName: "", // Clear search when changing regions
    });

    // Clear selected space when changing regions
    setSelectedSpace(null);
    setSelectedSpaceRegion(null);
    setSelectedSpaceDisplayName("");
    setSelectedSpaceBucketName("");
    setSelectedSpaceGxpFlag("");
    setSelectedDataAccountID("");
    setSelectedSpaceFileSystemType("");
  };

  const handleIndividualRegionChange = (region: string, checked: boolean) => {
    let newSelectedRegions: string[];

    if (checked) {
      newSelectedRegions = [region];
    } else {
      newSelectedRegions = ["all"];
    }

    handleRegionsChange(newSelectedRegions);
  };

  const handleViewObjects = async (
    spaceName: string,
    region: string,
    spacePrefix: string,
    bucketName: string,
    spaceDisplayName: string,
    sus_git_repo: string,
    sus_project_id: string,
    sus_project_link: string,
    sus_project_metadata: string,
    gxpFlag: string,
    data_account_id: string,
    metaDataUsers?: Array<{ user_email: string; access_level: string; }>,
    fileSystemType?: string,
  ) => {
    // Refresh token before setting state
    try {
      await getS3grantAccessToken(msalInstance, true);
    } catch (error) {
      console.error("Failed to refresh token before viewing space:", error);
    }
    
    setCurrentPrefix(spacePrefix);
    setSelectedSpace(spaceName);
    setSelectedSpaceRegion(region);
    setSelectedSpaceDisplayName(spaceDisplayName);
    setSelectedSpaceBucketName(bucketName);
    setSelectedSpaceGxpFlag(gxpFlag);
    setSelectedDataAccountID(data_account_id);
    setSelectedSpaceUserPermissions(metaDataUsers || []);
    setSusData({
      gitRepo: sus_git_repo,
      projectId: sus_project_id,
      projectLink: sus_project_link,
      susProjectMetadata: sus_project_metadata,
    });

    setSelectedSpaceFileSystemType(fileSystemType || "");
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 15,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            background: "#fafcff",
            border: "1px solid #e4e8f0",
            borderRadius: 8,
            padding: "8px 18px 8px 10px",
            minHeight: 40,
            boxShadow: "0 1px 4px #eaeaea",
            flexWrap: "wrap",
          }}
        >
          <Checkbox
            value="all"
            checked={selectedRegions.includes("all")}
            onChange={e => handleRegionsChange(["all"])}
            style={{
              fontWeight: 500,
              fontSize: 15,
              margin: 0,
              color: "#222",
            }}
          >
            ALL
          </Checkbox>

          {regionNames.map(r => (
            <Checkbox
              value={r}
              key={r}
              checked={
                selectedRegions.includes("all")
                  ? false
                  : selectedRegions.includes(r)
              }
              onChange={e => handleIndividualRegionChange(r, e.target.checked)}
              style={{
                fontWeight: 500,
                fontSize: 15,
                color: "#222",
                opacity: 1,
                margin: 0,
              }}
            >
              {r.toUpperCase()}
            </Checkbox>
          ))}

        </div>
        <Box>
          <Switch
            checked={filters.isMySpaces}
            checkedChildren="My owned spaces"
            unCheckedChildren="My owned spaces"
            onChange={handleMySpaces}
            className="space-list-switch"
          />
        </Box>
        <Box>
          <Switch
            checked={!!filters.isMyAccessibleSpaces}
            checkedChildren="My accessible spaces"
            unCheckedChildren="My accessible spaces"
            onChange={handleMyAccessibleSpaces}
            className="space-list-switch"
          />
        </Box>
      </div>

      <div className="flex-wrapper">
        <span style={{ fontSize: 12, color: "#888", marginLeft: 10 }} className="selected-regions">
          {selectedRegions.includes("all") || selectedRegions.length === 0
            ? "All regions selected (showing all)"
            : `Selected: ${selectedRegions.map(r => r.toUpperCase()).join(", ")}`}
        </span>

        <SpaceFolderGrid
          spaces={spaceList}
          useMySpaces={filters.isMySpaces}
          regionName={filters.selectedRegions.length === 1
            ? filters.selectedRegions[0]
            : filters.selectedRegions.length === 0 || filters.selectedRegions.includes("all")
              ? "All"
              : filters.selectedRegions.join(",")}
          domainName={domainName}
          viewObjects={handleViewObjects}
          onRefreshSpaces={onRefreshSpaces}
          isSpaceListFetching={isSpaceListFetching}
          searchTerm={filters.searchSpaceName}
          setSearchTerm={onSearchInputChange}
          pagination={pagination}
          filters={filters}
          onFiltersChange={onFiltersChange}
          sortOptions={sortOptions}
          onSortChange={onSortChange}
          onPaginationChange={onPaginationChange}
          handleSearchTermChange={onSearchInputChange}
          handleAutoRefreshSpaces={handleAutoRefreshSpaces}
          refreshKey={refreshKey} 
        />
      </div>

      {selectedSpace && (
        <Box>
          <Divider />
          <FileGrid
            domainName={domainName}
            selectedRegion={
              selectedRegions.length === 1
                ? selectedRegions[0]
                : selectedRegions.length === 0 || selectedRegions.includes("all")
                  ? "All"
                  : selectedRegions.join(",")
            }
            spaceName={selectedSpaceBucketName}
            spaceDisplayName={selectedSpaceDisplayName}
            currentPrefix={currentPrefix}
            selecteSpaceRegion={selectedSpaceRegion}
            selectedBucket={selectedSpaceBucketName}
            gxpFlag={selectedSpaceGxpFlag}
            DataAccountId={selectedDataAccountID}
            fileSystemType={selectedSpaceFileSystemType}
            onFolderClick={(selectedSpace, selected_region, prefix, bucketName, spaceDisplayName) => {
              handleViewObjects(selectedSpace, selected_region, prefix, bucketName, spaceDisplayName, susData?.gitRepo, susData?.projectId, susData?.projectLink, susData?.susProjectMetadata, selectedSpaceGxpFlag, selectedDataAccountID, selectedSpaceUserPermissions, selectedSpaceFileSystemType);
            }}
            onRefreshSpaces={onRefreshSpaces}
            setSelectedSpace={setSelectedSpace}
          />
          <Divider />
          <LinksSection 
            spaceDisplayName={selectedSpaceDisplayName} 
            sageMakerData={susData} 
            domainName={domainName}
            region={selectedSpaceRegion || undefined}
            bucketName={selectedSpaceBucketName}
            dataAccountId={selectedDataAccountID}
            gxpFlag={selectedSpaceGxpFlag}
          />
        </Box>
      )}
    </div>
  );
};

export default SpaceList;